<template>
	<div class="topbar">
		<div
			class="topbar__content"
			:style="`max-width: ${contentWidth}`"
		>
			<div
				v-click-outside="close"
				:aria-expanded="isOpen"
				:aria-label="menuLabel"
				class="topbar__title"
				:tabindex="menuItems.length > 0 ? 0 : -1"
				@click="toggleState"
				@keyup.enter="toggleState"
				@keyup.space="toggleState"
			>
				<div
					v-if="menuItems.length > 0"
					:data-anchor="`title-trigger-${_uid}`"
					class="topbar__popup-anchor"
				/>

				<h4 class="topbar__title-header">
					<helio-icon
						v-if="iconToken"
						color="sky-gray7"
						:token="iconToken"
						:weight="iconWeight"
					/>

					{{ title }}
				</h4>

				<div
					v-if="menuItems.length > 0"
					class="topbar__trigger"
					role="presentation"
				>
					<helio-icon
						:class="[
							'topbar__trigger-icon',
							{
								'topbar__trigger-icon--rotate': iconRotation,
							}
						]"
						:token="currentIcon"
						weight="light"
					/>
				</div>
			</div>

			<slot />
		</div>

		<div
			v-if="menuItems.length > 0"
			ref="content"
		>
			<div class="topbar__popup-wrapper">
				<helio-popup
					:active="isOpen"
					:anchor="`title-trigger-${_uid}`"
					class="topbar__popup"
					:close-on-outside-click="false"
					:gap="menuGap"
					:mobile-overlay="false"
					offset="right"
					position="bottom"
				>
					<template v-slot:content>
						<div class="topbar__vertical-navigation-wrapper">
							<helio-vertical-navigation
								:buttons="menuItems"
								@click="$emit('itemClick', $event)"
							/>
						</div>
					</template>
				</helio-popup>
			</div>
		</div>
	</div>
</template>

<script>
import Icon from '../icon/Icon.vue';

export default {
	name: 'Topbar',

	components: {
		'helio-icon': Icon,
	},

	props: {
		closedIcon: {
			type: String,
			required: false,
			default: 'angle-down',
			description: 'Sets which icon will be used to indicate a closed Topbar title',
		},

		contentWidth: {
			type: String,
			required: false,
			default: '100%',
			description: 'Sets the width of the Topbar in its container',
		},

		iconRotation: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the icon will rotate when the Topbar title is toggled open and closed.',
		},

		iconToken: {
			type: String,
			required: false,
			default: null,
			description: 'Sets whether the Topbar will include an icon to the left of the title and which icon will be displayed',
		},

		iconWeight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the left-hand icon',
		},

		menuGap: {
			type: Number,
			required: false,
			default: 16,
			description: 'Sets the distance between the Topbar menu and its anchor element',
		},

		menuItems: {
			type: Array,
			required: false,
			default() {
				return [];
			},
			validator: ((menuItems) => {
				let passed = true;

				menuItems.forEach((menuItem) => {
					const menuItemHasValue = typeof menuItem.value === 'string';
					const menuItemHasTitle = typeof menuItem.title === 'string';

					// Each object in the buttons array must include a
					// 'value' and a 'title' key/value pair.
					if (!menuItemHasValue || !menuItemHasTitle) {
						passed = false;
					}
				});

				return passed;
			}),
			description: 'Supplies data for the Topbar title menu options',
		},

		menuName: {
			type: String,
			required: false,
			default: '',
			description: 'Sets the name of the Topbar title menu',
		},

		openedIcon: {
			type: String,
			required: false,
			default: 'angle-up',
			description: 'Sets which icon will be used to indicate an open Topbar title',
		},

		title: {
			type: String,
			required: true,
			description: 'Sets the Topbar\'s title',
		},
	},

	slots: {
		default: {
			required: false,
			scoped: false,
			description: 'Supplies the content of the Topbar',
		},

		titleDropdown: {
			required: true,
			scoped: false,
			description: 'Supplies the content that will appear when the Topbar title is toggled open',
		},
	},

	data() {
		return {
			isOpen: false,
		};
	},

	computed: {
		currentIcon() {
			return this.isOpen ? this.openedIcon : this.closedIcon;
		},

		menuLabel() {
			return !this.isOpen ? `${this.$t('aria-label.open')} ${this.menuName}` : '';
		},
	},

	created() {
		this.isOpen = false;
	},

	mounted() {
		window.addEventListener('keyup', this.checkForEscape);
	},

	beforeDestroy() {
		window.removeEventListener('keyup', this.checkForEscape);
	},

	methods: {
		checkForEscape(event) {
			const { keyCode } = event;

			if (keyCode === 27 && this.isOpen) {
				this.close();
			}
		},

		close() {
			setTimeout(() => {
				this.isOpen = false;
				this.$emit('close');
			}, 200);
		},

		open() {
			this.isOpen = true;
			this.$emit('open');
		},

		toggleState() {
			if (!this.isOpen) {
				this.open();
			} else {
				this.close();
			}
		},
	},
};
</script>

<style lang="scss" src="./topbar.scss" />
